/**
 * Pure question helpers — ported from
 * student-dashboard-v2/src/app/(dashboard)/hooks/helper.ts so question routing
 * and text processing match the student website exactly.
 */
import type {SimpleQuestion} from './simpleTypes';

const MCQ_QUESTION_TYPES = ['COMPREHENSION', 'MULTIPLE_CHOICES'];
const TYPED_ANSWER_QUESTION_TYPES = [
  'FILL_IN_THE_BLANKS',
  'REARRANGE_SENTENCE',
];

export const shouldUseMCQModal = (question?: SimpleQuestion): boolean =>
  MCQ_QUESTION_TYPES.includes(question?.type ?? '');

export const shouldUseTypedAnswerModal = (question?: SimpleQuestion): boolean =>
  TYPED_ANSWER_QUESTION_TYPES.includes(question?.type ?? '');

export const getChoicesForQuestion = (question: SimpleQuestion): string[] => {
  if (!question.payload?.blanks || question.payload.blanks.length === 0) {
    return [];
  }
  if (question.payload.blanks.length === 1) {
    return question.payload.blanks[0].choices;
  }
  const allChoices = question.payload.blanks.flatMap(blank => blank.choices);
  return Array.from(new Set(allChoices));
};

export const removeDuplicateChoiceKeys = (questionText: string): string => {
  if (!questionText) return '';
  const seenKeys = new Set<string>();
  return questionText.replace(/CHOICES\d+/g, match => {
    if (seenKeys.has(match)) return '';
    seenKeys.add(match);
    return match;
  });
};

export const processQuestionText = (question: SimpleQuestion): string => {
  let questionText = question.questionText || question.AiText || '';

  if (question.type === 'COMPREHENSION') {
    questionText = removeDuplicateChoiceKeys(questionText);

    if (hasMultipleBlanks(question)) {
      return questionText;
    }

    const choicesPattern = /CHOICES\d+/g;
    if (choicesPattern.test(questionText)) {
      return questionText.replace(/CHOICES\d+/g, '{blank}');
    }

    const questionMarkIndex = questionText.indexOf('?');
    if (questionMarkIndex !== -1) {
      return questionText.substring(0, questionMarkIndex + 1);
    }
  }

  return questionText;
};

export const hasMultipleBlanks = (question: SimpleQuestion): boolean =>
  (question.payload?.blanks?.length || 0) > 1;

export const getChoiceGroups = (
  question: SimpleQuestion,
): Record<string, string[]> => {
  if (!question.payload?.blanks) return {};
  const groups: Record<string, string[]> = {};
  question.payload.blanks.forEach(blank => {
    groups[blank.name] = blank.choices;
  });
  return groups;
};

export const getBlankGroups = (question: SimpleQuestion) => {
  if (!question.payload?.blanks) return [];
  return question.payload.blanks.map(blank => ({
    key: blank.name,
    label: `${blank.name}:`,
    choices: blank.choices,
    selectedChoice: undefined,
  }));
};

export const formatComprehensionTitleText = (titleText: string): string => {
  if (!titleText) return '';
  const replacedKeys = new Set<string>();
  return titleText.replace(/CHOICES\d+/g, match => {
    if (replacedKeys.has(match)) return '';
    replacedKeys.add(match);
    return '_____';
  });
};
